// Dialog flow for user authentication (email, OTP, success)

import React, { useState } from 'react';
import ChooseMethodStep from './ChooseMethodStep';
import EmailStep from './EmailStep';
import OtpStep from './OtpStep';
import WelcomeStep from './WelcomeStep';
import type { AuthDialogStep } from '../../hooks/useAuthDialogStep';

type AuthDialogFlowProps = {
  step: AuthDialogStep;
  onStepChange: (step: AuthDialogStep) => void;
  onWelcomeContinue: () => void;
};

/**
 * Controlled renderer for the login dialog's steps. Owns no side effects:
 * post-auth orchestration (toasts, checkout resume, welcome transitions)
 * lives in `useAuthPostLogin`.
 */
const AuthDialogFlow: React.FC<AuthDialogFlowProps> = ({
  step,
  onStepChange,
  onWelcomeContinue,
}) => {
  // `pendingEmail` bridges the email step → otp step. It's internal because
  // the otp step can only be reached from the email step in this flow.
  const [pendingEmail, setPendingEmail] = useState('');

  if (step === 'welcome') {
    return <WelcomeStep onContinue={onWelcomeContinue} />;
  }

  if (step === 'otp') {
    return (
      <OtpStep
        email={pendingEmail}
        onChangeEmail={() => onStepChange('email')}
      />
    );
  }

  if (step === 'email') {
    return (
      <EmailStep
        onSubmit={(email) => {
          setPendingEmail(email);
          onStepChange('otp');
        }}
        onBack={() => onStepChange('choice')}
      />
    );
  }

  return <ChooseMethodStep onChooseEmail={() => onStepChange('email')} />;
};

export default AuthDialogFlow;
